import type { InjectionKey, Ref } from "vue";

import type { SuggestionRule } from "@/composables/useUrlFieldSuggestion";
import type { TranslationKey } from "@/i18n/types";

// An option in a SelectField or ToggleField. The value is what the model holds;
// the label is already translated.
export interface FieldOption<V extends string | number | boolean = string> {
  value: V;
  label: string;
  disabled?: boolean;
  // ToggleField only: this option starts a group of its own, set apart by this
  // word (the trust-policy chooser's "or" before Legacy).
  gapBefore?: string;
}

// What a view holds through a template ref on KitForm. The component is
// generic, so InstanceType cannot name it.
export interface KitFormHandle<TValues extends Record<string, unknown> = Record<string, unknown>> {
  submit: (event?: Event) => Promise<unknown>;
  resetForm: (state?: { values?: Partial<TValues> }) => void;
  setValues: (values: Partial<TValues>) => void;
  setFieldValue: (field: keyof TValues & string, value: unknown) => void;
  setFieldError: (field: keyof TValues & string, message: string | undefined) => void;
  values: TValues;
}

// Where a form's labels sit: above every control (the default, and all a dialog
// or a card column has room for), or beside it on a page wide enough, which a
// settings page asks for.
export type LabelPlacement = "above" | "beside";
export const KIT_FORM_LABELS: InjectionKey<Ref<LabelPlacement>> = Symbol("kitFormLabels");

// The name a field's native control carries: the last segment of its path
// ("Hostname" for "BackendConfig.kemp.Hostname"), as FormKit named its inputs,
// so the system tests' [name=…] selectors carry over. The full path is on the
// row's data-field.
export function nativeName(path: string): string {
  const segments = path.split(/[.[\]]/).filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

// A field's "did you mean" hint: the rules that read its value, whether the hint
// offers a fix that writes the suggested value back, and an already translated
// word for that fix button when the value itself would not say it.
export interface FieldSuggest {
  rules: SuggestionRule[];
  accept?: boolean;
  linkText?: string;
}

// One name in an AgentNameField's list: something the agent reported, and what
// kind of thing it is (hash:ip), shown beside it.
export interface ReportedName {
  value: string;
  detail?: string;
}

// A message a kit field translates when it shows it: an i18n key and its values.
export interface FieldMsg {
  key: TranslationKey;
  params?: Record<string, string | number>;
}

// One labelled fact a KeyFileField lists under a key. A string value is data
// from the file (shown monospaced); a FieldMsg is wording ("Included, not
// shown"), shown quietly.
export interface KeyFileFact {
  label: FieldMsg;
  value: string | FieldMsg;
}

// What a KeyFileField's `read` makes of a pasted or dropped file: the key it
// is, named by `title` (who it signs in as) and `facts`, never by its secret;
// or what is wrong with it, said as what the file turned out to be.
export type KeyFileReading = { ok: true; title: string; facts?: KeyFileFact[] } | { ok: false; problem: FieldMsg };

// One way a TransportField's address can be reached: LDAPS, StartTLS or Plain
// for LDAP, UDP or TCP for GELF.
export interface Transport {
  id: string;
  // The URL scheme it is spelt with, without "://".
  scheme: string;
  label: string;
  description?: string;
  // What it sets the field's `flagName` to, where two transports share a scheme
  // (StartTLS and Plain are both ldap://).
  flag?: boolean;
  // false when the connection has no TLS, which stands the certificate menu down.
  tls?: boolean;
  // Said under the field while this transport is picked.
  note?: string;
}

// One region in a RegionField's list: the code that is stored and the
// provider's own name for it ("Asia Pacific (Sydney)").
export interface CloudRegion {
  code: string;
  name: string;
}
